import { useCallback, useEffect, useMemo } from 'react';
import { useGroupStore } from '../stores/group-store';
import { useMessage } from '@/shared/hooks/useMessage';
import {
    useGetAllGroups,
    useGetAllAccessLevels,
    useCreateGroup,
    useUpdateGroup
} from '@/shared/api/client/nub-user-resource';
import { NubGroupDTO } from '../types';

export const useGroup = () => {
    const store = useGroupStore();
    const message = useMessage();

    // Queries
    const {
        data: groupsResponse,
        isLoading: isLoadingGroups,
        error: groupsError,
        refetch: refetchGroups
    } = useGetAllGroups({
        query: {
            enabled: true,
            staleTime: 5 * 60 * 1000,
            // @ts-ignore
            cacheTime: 10 * 60 * 1000
        }
    });

    const {
        data: accessLevelsResponse,
        isLoading: isLoadingAccessLevels
    } = useGetAllAccessLevels({
        query: {
            enabled: true,
            staleTime: 30 * 60 * 1000,
            // @ts-ignore
            cacheTime: 60 * 60 * 1000
        }
    });

    // Mutations
    const createMutation = useCreateGroup();
    const updateMutation = useUpdateGroup();

    // Sync backend data with store
    useEffect(() => {
        if (groupsResponse) {
            store.setGroups(groupsResponse);
        }
    }, [groupsResponse]);

    useEffect(() => {
        if (accessLevelsResponse) {
            store.setAccessLevels(accessLevelsResponse);
        }
    }, [accessLevelsResponse]);

    // CRUD Operations
    const createGroup = useCallback(async (data: NubGroupDTO) => {
        try {
            const response = await createMutation.mutateAsync({ data });
            message.success('Group created successfully');
            await refetchGroups();
            store.setModalOpen(false);
            return response.data;
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to create group');
            throw error;
        }
    }, [createMutation, message, refetchGroups]);

    const updateGroup = useCallback(async (code: string, data: NubGroupDTO) => {
        try {
            const response = await updateMutation.mutateAsync({ code, data });
            message.success('Group updated successfully');
            await refetchGroups();
            store.setModalOpen(false);
            return response.data;
        } catch (error: any) {
            message.error(error?.response?.data?.message || 'Failed to update group');
            throw error;
        }
    }, [updateMutation, message, refetchGroups]);

    // Utility functions
    const handleGroupAction = useCallback((
        group: NubGroupDTO | null,
        mode: 'view' | 'add' | 'edit' | 'delete'
    ) => {
        store.setActiveGroup(group);
        store.setActionMode(mode);
        store.setModalOpen(true);
        if (group?.accessLevels) {
            store.setSelectedAccessLevels(
                // @ts-ignore
                group.accessLevels.map(al => al.code)
            );
        } else {
            store.setSelectedAccessLevels([]);
        }
    }, [store]);

    return {
        // State
        groups: store.groups,
        // @ts-ignore
        activeGroup: store.activeGroup,
        // @ts-ignore
        accessLevels: store.accessLevels,
        // @ts-ignore
        selectedAccessLevels: store.selectedAccessLevels,
        searchTerm: store.searchTerm,
        // @ts-ignore
        actionMode: store.actionMode,
        // @ts-ignore
        isModalOpen: store.isModalOpen,
        isLoading: isLoadingGroups || isLoadingAccessLevels,
        error: groupsError,

        // Actions
        setSearchTerm: store.setSearchTerm,
        setSelectedAccessLevels: store.setSelectedAccessLevels,
        setModalOpen: store.setModalOpen,
        handleGroupAction,

        // CRUD Operations
        createGroup,
        updateGroup,
        refetchGroups,
    };
};